import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, ExternalLink, AlertTriangle, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { KpiCard } from '../components/UI';
import { api, moneyFull, pct } from '../lib/api';

// Trailing N months (inclusive) in YYYY-MM.
function trailingWindow(n = 3) {
  const now = new Date();
  const end = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const d = new Date(now.getFullYear(), now.getMonth() - (n - 1), 1);
  const start = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  return { start, end };
}

// Roll up channel rows across the selected window → one row per channel.
function rollup(rows) {
  const bucket = {};
  for (const r of rows || []) {
    const k = r.channel;
    const b = bucket[k] || {
      channel: k, channel_label: r.channel_label,
      spend: 0, attributed_revenue: 0, leads: 0, deals_closed: 0,
      has_spend_tracking: r.has_spend_tracking, deep_link: r.deep_link,
    };
    b.spend += r.spend || 0;
    b.attributed_revenue += r.attributed_revenue || 0;
    b.leads += r.leads || 0;
    b.deals_closed += r.deals_closed || 0;
    b.deep_link = r.deep_link || b.deep_link;
    bucket[k] = b;
  }
  return Object.values(bucket).map((b) => ({
    ...b,
    roas: b.spend > 0 ? +(b.attributed_revenue / b.spend).toFixed(2) : null,
  })).sort((a, b) => (b.spend || 0) - (a.spend || 0));
}

export default function MarketingROI() {
  const [window, setWindow] = useState(() => trailingWindow(3));
  const [summary, setSummary] = useState(null);
  const [mix, setMix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aliases, setAliases] = useState([]);   // [{from, to, note}] — channel merges
  const [showAliases, setShowAliases] = useState(false);

  const fetchAll = () => {
    setLoading(true);
    const p = `from=${window.start}&to=${window.end}`;
    Promise.all([
      api.get(`/marketing/summary?${p}`),
      api.get(`/marketing/mix?${p}`),
      api.get('/marketing/aliases').catch(() => ({ data: [] })),
    ])
      .then(([s, m, a]) => { setSummary(s.data); setMix(m.data); setAliases(a.data || []); })
      .catch(() => toast.error('Could not load Marketing ROI.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, [window.start, window.end]);

  const refresh = async () => {
    try { await api.post('/marketing/refresh'); } catch { /* ignore */ }
    fetchAll();
  };

  // Apply channel aliases so upstream inconsistencies (e.g. "Facebook Ads"
  // on the spend feed vs. "meta" on the HubSpot lead feed) get merged into
  // a single row. Advisor Feb 2026: this was the Meta $0-spend bug.
  const aliasMap = useMemo(() => {
    const m = {};
    for (const a of aliases || []) if (a?.from && a?.to) m[a.from.toLowerCase()] = a.to;
    return m;
  }, [aliases]);
  const applyAlias = (chan) => (chan && aliasMap[String(chan).toLowerCase()]) || chan;
  const remappedChannels = useMemo(
    () => (summary?.data?.channels || []).map((r) => ({ ...r, channel: applyAlias(r.channel) })),
    [summary, aliasMap],  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const channels = useMemo(() => rollup(remappedChannels), [remappedChannels]);
  const totals = useMemo(() => {
    const t = channels.reduce((a, r) => ({
      spend: a.spend + (r.spend || 0),
      rev: a.rev + (r.attributed_revenue || 0),
      leads: a.leads + (r.leads || 0),
    }), { spend: 0, rev: 0, leads: 0 });
    t.blendedRoas = t.spend > 0 ? +(t.rev / t.spend).toFixed(2) : null;
    return t;
  }, [channels]);

  const belowThreshold = channels.filter((c) => c.spend > 0 && (c.roas ?? 0) < 1.5).length;
  const fetchedAt = summary?.fetched_at;
  const available = summary?.available !== false && mix?.available !== false;
  const concentrationRisk = mix?.data?.concentration_risk === true;
  const topChannel = mix?.data?.top_channel;
  const topShare = mix?.data?.top_channel_share;

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="label-editorial">Marketing ROI</div>
            <h1 className="text-3xl font-bold text-indie-navy mt-1 flex items-center gap-2">
              <Megaphone size={26} /> What&apos;s every marketing dollar doing?
            </h1>
            <p className="text-sm text-indie-ink/65 mt-1 max-w-3xl">
              Financial lens on marketing spend — sourced live from Indie Marketing Intel.
              Threshold: ROAS ≥ 1.5x (per Stephanie).
            </p>
          </div>
          <div className="flex items-center gap-3">
            <WindowPicker window={window} setWindow={setWindow} />
            <button
              type="button"
              onClick={() => setShowAliases((s) => !s)}
              data-testid="mroi-aliases-toggle"
              className="inline-flex items-center gap-2 border border-indie-navy/20 bg-white text-indie-ink px-3 py-1.5 text-xs uppercase tracking-wider font-semibold hover:bg-indie-cream"
              title="Merge channel labels (e.g. Facebook Ads → meta) so attribution lines up"
            >
              Channel aliases {aliases?.length ? `(${aliases.length})` : ''}
            </button>
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              data-testid="mroi-refresh"
              className="inline-flex items-center gap-2 border border-indie-navy/20 bg-white text-indie-ink px-3 py-1.5 text-xs uppercase tracking-wider font-semibold hover:bg-indie-cream disabled:opacity-50"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {showAliases && (
          <ChannelAliasEditor aliases={aliases} onSaved={fetchAll} />
        )}

        {!available && (
          <div className="bg-indie-cream border-l-4 border-indie-neg-dark p-4 text-sm text-indie-ink/80" data-testid="mroi-unavailable">
            <div className="font-semibold text-indie-neg-dark mb-1">Marketing data unavailable</div>
            <div>{summary?.reason || mix?.reason || 'Indie Marketing Intel is not responding right now. Try Refresh in a minute.'}</div>
          </div>
        )}

        {concentrationRisk && available && (
          <div className="bg-indie-yellow/25 border-l-4 border-indie-yellow p-4 flex items-start gap-3" data-testid="mroi-concentration-warning">
            <AlertTriangle className="text-indie-ink shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-indie-ink">
              <strong>Channel concentration risk.</strong>{' '}
              <span className="capitalize">{(topChannel || '').replace(/_/g, ' ')}</span> is{' '}
              <strong className="num-tabular">{pct(topShare, 0)}</strong> of leads. Stephanie flags anything above 50% — start
              diversifying before the next quarter.
            </div>
          </div>
        )}

        {/* KPI tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Total spend" value={moneyFull(totals.spend)} sub={`${window.start} → ${window.end}`} testId="mroi-kpi-spend" />
          <KpiCard label="Attributed revenue" value={moneyFull(totals.rev)} sub={`${totals.leads} leads`} testId="mroi-kpi-rev" />
          <KpiCard
            label="Blended ROAS"
            value={totals.blendedRoas != null ? `${totals.blendedRoas.toFixed(2)}x` : '—'}
            sub={totals.blendedRoas != null ? (totals.blendedRoas >= 1.5 ? '✓ above 1.5x threshold' : '⚠ below 1.5x threshold') : ''}
            testId="mroi-kpi-roas"
          />
          <KpiCard
            label="Channels below threshold"
            value={belowThreshold}
            sub={belowThreshold === 0 ? 'All channels performing' : `${belowThreshold} need review`}
            testId="mroi-kpi-below"
          />
        </div>

        {/* Paid channels table */}
        <div className="bg-white border-t-4 border-indie-navy" data-testid="mroi-channels-table">
          <div className="px-5 py-3 border-b border-indie-navy/10 flex items-baseline justify-between">
            <div className="label-editorial">Paid channels</div>
            {fetchedAt && (
              <div className="text-[10px] text-indie-ink/45 uppercase tracking-wider">
                Data as of {new Date(fetchedAt).toLocaleString()} {summary?.cached ? '(cached)' : ''}
              </div>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-indie-navy text-indie-cream">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-medium">Channel</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider font-medium">Spend</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider font-medium">Attributed rev</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider font-medium">Leads</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider font-medium">ROAS</th>
                <th className="px-3 py-2 text-center text-[10px] uppercase tracking-wider font-medium">Status</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider font-medium">Detail</th>
              </tr>
            </thead>
            <tbody>
              {channels.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-indie-ink/55">
                  {loading ? 'Loading…' : 'No channels reported for this window.'}
                </td></tr>
              )}
              {channels.map((c) => {
                const hasSpend = (c.spend || 0) > 0;
                const hasRev = (c.attributed_revenue || 0) > 0;
                const roas = c.roas;
                let statusPill = null;
                // Attribution-mismatch pill (advisor pass Feb 2026): flag any
                // channel that shows one side of the equation but not the other.
                // Meta was the flagship case — attributed revenue showed up in
                // HubSpot but $0 spend because the platform tag on the spend
                // side didn't line up with the HubSpot lead-source label.
                const attributionMismatch = (hasSpend && !hasRev) || (!hasSpend && hasRev);
                if (!hasSpend && !hasRev) statusPill = <Pill tone="muted">No data</Pill>;
                else if (!hasSpend && hasRev) statusPill = <Pill tone="red">⚠ Revenue, no spend (attribution)</Pill>;
                else if (hasSpend && !hasRev) statusPill = <Pill tone="red">⚠ Spend, no revenue (attribution)</Pill>;
                else if (roas == null) statusPill = <Pill tone="muted">No revenue attribution</Pill>;
                else if (roas >= 1.5) statusPill = <Pill tone="green">✓ Above 1.5x</Pill>;
                else statusPill = <Pill tone="red">⚠ Below 1.5x</Pill>;
                const link = c.deep_link?.campaigns || c.deep_link?.dashboard;
                return (
                  <tr key={c.channel} className="border-b border-indie-navy/5 hover:bg-indie-cream/40" data-testid={`mroi-row-${c.channel}`}>
                    <td className="px-3 py-2.5 font-semibold text-indie-navy">{c.channel_label}</td>
                    <td className="px-3 py-2.5 text-right num-tabular">{moneyFull(c.spend)}</td>
                    <td className="px-3 py-2.5 text-right num-tabular">{moneyFull(c.attributed_revenue)}</td>
                    <td className="px-3 py-2.5 text-right num-tabular">{c.leads || 0}</td>
                    <td className="px-3 py-2.5 text-right num-tabular font-semibold">{roas != null ? `${roas.toFixed(2)}x` : '—'}</td>
                    <td className="px-3 py-2.5 text-center">{statusPill}</td>
                    <td className="px-3 py-2.5 text-right">
                      {link ? (
                        <a href={link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indie-cobalt hover:text-indie-navy" data-testid={`mroi-detail-${c.channel}`}>
                          View detail <ExternalLink size={11} />
                        </a>
                      ) : <span className="text-xs text-indie-ink/35">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Channel mix */}
        {mix?.available && mix.data && (
          <div className="bg-white border-t-4 border-indie-cobalt p-5" data-testid="mroi-mix">
            <div className="label-editorial mb-3">Lead source mix · {mix.data.total_leads} leads</div>
            <div className="space-y-1.5">
              {(mix.data.mix || []).sort((a, b) => b.leads - a.leads).map((m) => {
                const share = m.share || 0;
                const overThreshold = share > 0.5;
                return (
                  <div key={m.channel} className="grid grid-cols-[140px_1fr_100px_60px] gap-3 items-center text-sm">
                    <div className="capitalize text-indie-ink/85">{(m.channel || '').replace(/_/g, ' ')}</div>
                    <div className="h-4 bg-indie-cream relative overflow-hidden">
                      <div className={`h-full ${overThreshold ? 'bg-indie-neg-dark' : 'bg-indie-cobalt'}`} style={{ width: `${share * 100}%` }} />
                    </div>
                    <div className="text-right num-tabular text-indie-ink/85">{m.leads || 0} leads</div>
                    <div className={`text-right num-tabular font-semibold ${overThreshold ? 'text-indie-neg-dark' : 'text-indie-ink'}`}>{pct(share, 0)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
  );
}

function WindowPicker({ window, setWindow }) {
  const presets = [
    { label: 'Last 3 mo', months: 3 },
    { label: 'Last 6 mo', months: 6 },
    { label: 'YTD', months: null },
  ];
  const pick = (n) => {
    if (n == null) {
      const now = new Date();
      setWindow({ start: `${now.getFullYear()}-01`, end: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}` });
    } else setWindow(trailingWindow(n));
  };
  return (
    <div className="flex items-center gap-1" data-testid="mroi-window">
      {presets.map((p) => (
        <button
          key={p.label}
          type="button"
          onClick={() => pick(p.months)}
          className="text-xs uppercase tracking-wider font-semibold px-2.5 py-1.5 border border-indie-navy/15 bg-white hover:bg-indie-cream"
        >
          {p.label}
        </button>
      ))}
      <span className="text-[10px] text-indie-ink/55 ml-2 num-tabular">{window.start} → {window.end}</span>
    </div>
  );
}

function Pill({ tone, children }) {
  const cls = tone === 'green' ? 'bg-indie-evergreen/15 text-indie-evergreen'
    : tone === 'red' ? 'bg-indie-neg/15 text-indie-neg-dark'
    : 'bg-indie-ink/10 text-indie-ink/65';
  return <span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${cls}`}>{children}</span>;
}


// ChannelAliasEditor — admin-editable list of upstream channel labels to
// merge into a canonical channel key. Fixes the Meta $0-spend attribution
// bug when upstream data uses different labels for the same channel.
function ChannelAliasEditor({ aliases, onSaved }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!from.trim() || !to.trim()) {
      toast.error('Both "from" and "to" are required.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/marketing/aliases', { from: from.trim(), to: to.trim() });
      setFrom(''); setTo('');
      toast.success('Alias saved. Refreshing…');
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save alias.');
    } finally {
      setSaving(false);
    }
  };
  const remove = async (row) => {
    if (!row?.from) return;
    try {
      await api.delete(`/marketing/aliases/${encodeURIComponent(row.from)}`);
      toast.success('Alias removed.');
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove alias.');
    }
  };
  return (
    <div className="bg-indie-cream/60 border border-indie-navy/10 p-4" data-testid="mroi-alias-editor">
      <div className="label-editorial mb-1">Channel aliases</div>
      <p className="text-xs text-indie-ink/65 mb-3">
        Merge upstream channel labels into a single row. Example: <code className="text-[11px]">Facebook Ads → meta</code> so
        Meta spend + HubSpot Meta-attributed revenue collapse into one line.
      </p>
      <div className="flex items-center gap-2 mb-3">
        <input
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          placeholder="Upstream label (e.g. Facebook Ads)"
          data-testid="mroi-alias-from"
          className="flex-1 border border-indie-navy/20 px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-indie-navy"
        />
        <span className="text-xs text-indie-ink/55">→</span>
        <input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="Canonical channel (e.g. meta)"
          data-testid="mroi-alias-to"
          className="flex-1 border border-indie-navy/20 px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-indie-navy"
        />
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="mroi-alias-save"
          className="bg-indie-yellow text-indie-ink font-semibold uppercase tracking-wider text-xs px-3 py-1.5 hover:bg-indie-yellow/85 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Add'}
        </button>
      </div>
      {(aliases?.length || 0) === 0 ? (
        <div className="text-xs text-indie-ink/55">No aliases configured yet.</div>
      ) : (
        <ul className="space-y-1 text-sm">
          {aliases.map((a) => (
            <li key={a.from} className="flex items-center justify-between border-b border-indie-navy/5 py-1" data-testid={`mroi-alias-row-${a.from}`}>
              <span><code className="text-xs">{a.from}</code> → <code className="text-xs font-semibold">{a.to}</code></span>
              <button
                type="button"
                onClick={() => remove(a)}
                className="text-[10px] text-indie-neg-dark hover:underline uppercase tracking-wider"
                data-testid={`mroi-alias-remove-${a.from}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
